/**
 * Branded listing pages (Red Olive vs StayJade) — resolved from ?brand= URL + master brands list.
 *
 * ---------------------------------------------------------------------------
 * Hardcoded category / filter tokens (URL ?category= uses `tag` column key).
 * Admin stores Tag **IDs** on properties; names below are fallbacks so UI
 * tokens still match real Tag.name / any casing. Aliases are matched with
 * normalizeTagCompact() (letters+digits only, case-insensitive).
 * ---------------------------------------------------------------------------
 *
 * Red Olive — category tokens & aliases:
 *   apartments   → Apartments, APARTMENTS, apartment, apts
 *   penthouses   → Penthouses, PENTHOUSES, penthouse
 *   longstay     → Long Stay, longstay, LONGSTAY, long-stay
 *   corporate    → Corporate, CORPORATE
 *   premium      → Premium, PREMIUM
 *
 * StayJade — category tokens & aliases:
 *   poolvillas   → Pool Villas, POOL VILLAS, pool villa, PoolVillas
 *   beach        → Beach, BEACH
 *   farmstay     → Farm Stay, farmstay, FARMSTAY, farm-stay
 *   events       → Events, EVENTS
 *   hillretreat  → Hill Retreat, hillretreat, HILLRETREAT, hill retreat
 *
 * Stay-type toggles (client-side, same ID + name rules):
 *   shortstay    → Short Stay, SHORT STAY, ShortStay
 *   longstay     → (shared with category)
 *   monthly      → Monthly, MONTHLY, Monthly Stay
 *
 * Editor’s Pick featured row:
 *   editorpick   → editor pick, Editor's Pick, Editors Pick, EDITORS PICK, editorspick
 *
 * Brand paths (canonical):
 *   /stayjade     → StayJade variant
 *   /red-olive    → Red Olive variant
 * Legacy query (redirected): stayjade, stay-jade, red-olive, redolive
 */

import { normalizeTagCompact, resolveTagIdFromCatalog } from './propertyTagUtils';
import { PUBLIC_BRAND_STAYJADE, PUBLIC_BRAND_RED_OLIVE } from '../../constants/publicBrands';

/** Public brand slugs — keep aligned with Navbar / listing URLs. */
export const PUBLIC_BRAND_QUERY_STAYJADE = 'stayjade';
export const PUBLIC_BRAND_QUERY_RED_OLIVE = 'red-olive';

/** Canonical root paths: jadecaps.com/stayjade, jadecaps.com/red-olive */
export const PUBLIC_BRAND_PATH_STAYJADE = '/stayjade';
export const PUBLIC_BRAND_PATH_RED_OLIVE = '/red-olive';

export const BRAND_VARIANT = {
  RED_OLIVE: 'redOlive',
  STAY_JADE: 'stayJade',
  /** Default `/properties` — same shell + hero as branded listings */
  JADE_CAPS: 'jadeCaps',
};

/** `data-listing-brand` on the shared hero (matches `property-list.reference.css`). */
export const LISTING_BRAND_KEY = {
  RED_OLIVE: 'redOlive',
  STAY_JADE: 'stayJade',
  JADE_CAPS: 'jadeCaps',
};

/** Default hero / brand logo when API has no brand image (public/favicon.ico). */
export const JADECAPS_FALLBACK_LOGO = `${process.env.PUBLIC_URL || ''}/favicon.ico`;

/**
 * Property type sidebar filters — `ListingType.id` from admin / consumer API.
 * Apartment (3), Villa (13), All suite (1), Bungalow (23).
 */
export const LISTING_TYPE_FILTERS = [
  { id: '3', label: 'Apartment' },
  { id: '13', label: 'Villa' },
  { id: '1', label: 'All Suite' },
  { id: '23', label: 'Bungalow' },
];

/** Cities shown as pills — match ListingCity.name in DB (aliases help Bangalore/Bengaluru). */
export const BRANDED_CITY_OPTIONS = [
  { label: 'Bengaluru', aliases: ['bengaluru', 'bangalore'] },
  { label: 'Hyderabad', aliases: ['hyderabad'] },
  { label: 'Goa', aliases: ['goa'] },
  { label: 'Kolkata', aliases: ['kolkata', 'calcutta'] },
  { label: 'Chennai', aliases: ['chennai', 'madras'] },
];

/**
 * Hero tabs: `tag` is the default URL token; `matchNames` are extra Tag.name / label variants.
 */
export const CATEGORY_TABS = {
  [BRAND_VARIANT.JADE_CAPS]: [
    { label: 'All Properties', kind: 'all', tag: null, matchNames: [] },
    ...BRANDED_CITY_OPTIONS.map((o) => ({
      label: o.label,
      kind: 'city',
      cityValue: o.label,
      matchNames: [o.label, ...(o.aliases || [])],
    })),
  ],
  [BRAND_VARIANT.RED_OLIVE]: [
    { label: 'All Properties', kind: 'all', tag: null, matchNames: [] },
    { label: 'Bengaluru', kind: 'city', cityValue: 'Bangalore', matchNames: [] },
    { label: 'Hyderabad', kind: 'city', cityValue: 'Hyderabad', matchNames: [] },
    {
      label: 'Apartments',
      kind: 'tag',
      tag: 'apartments',
      matchNames: ['Apartments', 'APARTMENTS', 'apartment', 'apts', 'Apts'],
    },
    {
      label: 'Penthouses',
      kind: 'tag',
      tag: 'penthouses',
      matchNames: ['Penthouses', 'PENTHOUSES', 'penthouse', 'Penthouse'],
    },
    {
      label: 'Long Stay',
      kind: 'tag',
      tag: 'longstay',
      matchNames: ['Long Stay', 'LONG STAY', 'LongStay', 'long-stay', 'LONGSTAY'],
    },
    {
      label: 'Corporate Retreats',
      kind: 'tag',
      tag: 'corporate',
      matchNames: ['Corporate', 'CORPORATE', 'Corporate Retreats', 'corporate retreats'],
    },
  ],
  [BRAND_VARIANT.STAY_JADE]: [
    { label: 'All Properties', kind: 'all', tag: null, matchNames: [] },
    {
      label: 'Pool Villas',
      kind: 'tag',
      tag: 'poolvillas',
      matchNames: ['Pool Villas', 'POOL VILLAS', 'pool villa', 'PoolVillas', 'pool villas'],
    },
    {
      label: 'Beach',
      kind: 'tag',
      tag: 'beach',
      matchNames: ['Beach', 'BEACH', 'Beachfront', 'beach front'],
    },
    {
      label: 'Farm Stay',
      kind: 'tag',
      tag: 'farmstay',
      matchNames: ['Farm Stay', 'FARM STAY', 'FarmStay', 'farm-stay', 'farmstay', 'Farmstay'],
    },
    {
      label: 'Events',
      kind: 'tag',
      tag: 'events',
      matchNames: ['Events', 'EVENTS', 'Event', 'event'],
    },
    {
      label: 'Hill Retreat',
      kind: 'tag',
      tag: 'hillretreat',
      matchNames: ['Hill Retreat', 'HILL RETREAT', 'HillRetreat', 'hill retreat', 'hillretreat'],
    },
  ],
};

/** Stay filters — client-side; `propertyHasTag` uses IDs + catalog names. */
export const STAY_TYPE_TAG_DEFS = {
  shortStay: {
    token: 'shortstay',
    matchNames: ['Short Stay', 'SHORT STAY', 'ShortStay', 'short-stay', 'SHORTSTAY'],
  },
  longStay: {
    token: 'longstay',
    matchNames: ['Long Stay', 'LONG STAY', 'LongStay', 'long-stay', 'LONGSTAY'],
  },
  monthly: {
    token: 'monthly',
    matchNames: ['Monthly', 'MONTHLY', 'Monthly Stay', 'MONTHLY STAY', 'monthly stay'],
  },
};

/** Featured card — any of these names / token on the property’s Tag IDs. */
export const EDITOR_PICK_TAG_DEF = {
  token: 'editorpick',
  matchNames: [
    'Editor Pick',
    'EDITOR PICK',
    "Editor's Pick",
    'EDITORS PICK',
    'Editors Pick',
    'editors pick',
    'EditorsPick',
    'editorpick',
    'editorspick',
  ],
};

/** @deprecated use EDITOR_PICK_TAG_DEF + propertyHasTag */
export const EDITOR_PICK_TAG = EDITOR_PICK_TAG_DEF.token;

/** @deprecated use STAY_TYPE_TAG_DEFS */
export const STAY_TAG_SLUGS = {
  shortStay: STAY_TYPE_TAG_DEFS.shortStay.token,
  longStay: STAY_TYPE_TAG_DEFS.longStay.token,
  monthly: STAY_TYPE_TAG_DEFS.monthly.token,
};

function normalizeBrandQuery(q) {
  return String(q || '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]+/g, '');
}

function classifyBrandRow(b) {
  const slug = normalizeBrandQuery(b.slug || '');
  const name = String(b.name || '')
    .toLowerCase()
    .replace(/[\s\-_]+/g, '');
  if ((slug.includes('red') && slug.includes('olive')) || name.includes('redolive')) {
    return BRAND_VARIANT.RED_OLIVE;
  }
  if ((slug.includes('stay') && slug.includes('jade')) || name.includes('stayjade')) {
    return BRAND_VARIANT.STAY_JADE;
  }
  return null;
}

function brandRowMatchesQuery(b, compact) {
  if (!compact) return false;
  const slug = normalizeBrandQuery(b.slug || '');
  const nameNorm = normalizeBrandQuery(b.name || '');
  const idNorm = normalizeBrandQuery(String(b.id ?? ''));
  if (slug && slug === compact) return true;
  if (idNorm && idNorm === compact) return true;
  if (nameNorm && nameNorm === compact) return true;
  return false;
}

/** Canonical listing path for a brand slug / name / variant key. */
export function getCanonicalBrandListingPath(brandQuery, publicBrands = []) {
  const variant =
    brandQuery === BRAND_VARIANT.STAY_JADE || brandQuery === BRAND_VARIANT.RED_OLIVE
      ? brandQuery
      : resolveListingBrandVariant(brandQuery, publicBrands);
  if (variant === BRAND_VARIANT.STAY_JADE) return PUBLIC_BRAND_PATH_STAYJADE;
  if (variant === BRAND_VARIANT.RED_OLIVE) return PUBLIC_BRAND_PATH_RED_OLIVE;
  return null;
}

/**
 * Brand slug from a root vanity path (`/stayjade`, `/red-olive`, aliases).
 * Returns null for `/properties` and other non-brand paths.
 */
export function resolveBrandSlugFromPathname(pathname) {
  const segment = String(pathname || '')
    .replace(/\/+$/, '')
    .split('/')
    .filter(Boolean)[0];
  if (!segment || segment === 'properties') return null;
  const variant = resolveListingBrandVariant(segment, []);
  if (variant === BRAND_VARIANT.STAY_JADE) return PUBLIC_BRAND_QUERY_STAYJADE;
  if (variant === BRAND_VARIANT.RED_OLIVE) return PUBLIC_BRAND_QUERY_RED_OLIVE;
  return null;
}

/**
 * Build listing href for a brand — root path without `?brand=`.
 * @param {string} brandQueryOrVariant
 * @param {string|URLSearchParams} [search]
 */
export function buildBrandListingHref(brandQueryOrVariant, search = '', publicBrands = []) {
  const path =
    getCanonicalBrandListingPath(brandQueryOrVariant, publicBrands) || '/properties';
  const q = new URLSearchParams(
    typeof search === 'string' ? search.replace(/^\?/, '') : search.toString(),
  );
  q.delete('brand');
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Resolve branded listing variant from URL ?brand= / path slug and optional master brand rows.
 */
export function resolveListingBrandVariant(brandQuery, publicBrands = []) {
  const compact = normalizeBrandQuery(brandQuery);
  if (!compact) return null;

  if (compact === normalizeBrandQuery(PUBLIC_BRAND_QUERY_STAYJADE)) {
    return BRAND_VARIANT.STAY_JADE;
  }
  if (compact === normalizeBrandQuery(PUBLIC_BRAND_QUERY_RED_OLIVE)) {
    return BRAND_VARIANT.RED_OLIVE;
  }

  if (compact.includes('stay') && compact.includes('jade')) {
    return BRAND_VARIANT.STAY_JADE;
  }
  if (compact.includes('red') && compact.includes('olive')) {
    return BRAND_VARIANT.RED_OLIVE;
  }

  for (const b of publicBrands || []) {
    if (!brandRowMatchesQuery(b, compact)) continue;
    const variant = classifyBrandRow(b);
    if (variant) return variant;
  }

  if (/^\d+$/.test(compact)) {
    const byId = (publicBrands || []).find((b) => String(b.id) === compact);
    if (byId) {
      const variant = classifyBrandRow(byId);
      if (variant) return variant;
    }
  }

  return null;
}

/**
 * Map ?category= token to a value for the consumer API `tag` param (prefer Tag id from master list).
 */
export function resolveBrandedCategoryForApi(categoryToken, variant, masterTags = []) {
  if (!categoryToken || !variant) return categoryToken || null;
  const tabs = CATEGORY_TABS[variant] || [];
  const compactIn = normalizeTagCompact(categoryToken);
  const tab = tabs.find((t) => {
    if (!t || t.tag == null) return false;
    if (normalizeTagCompact(t.tag) === compactIn) return true;
    return (t.matchNames || []).some((m) => normalizeTagCompact(m) === compactIn);
  });
  if (!tab || tab.tag == null) return categoryToken;

  const tryOrder = [categoryToken, tab.tag, ...(tab.matchNames || [])];
  for (const c of tryOrder) {
    const id = resolveTagIdFromCatalog(c, masterTags);
    if (id) return id;
  }
  return tab.tag;
}

export function resolveCityIdForBranded(label, cities = []) {
  if (!label || !cities?.length) return null;
  const opt = BRANDED_CITY_OPTIONS.find((o) => o.label === label);
  const needles = opt ? [label.toLowerCase(), ...(opt.aliases || [])] : [label.toLowerCase()];

  const row = cities.find((c) => {
    const n = String(c.name || '').toLowerCase();
    return needles.some((needle) => n === needle || n.includes(needle));
  });
  return row ? row.id : null;
}

/**
 * `brand` query param for GET /property/consumer.
 * Admin stores `Property.brands` as comma-separated Brand **IDs** (e.g. "1").
 * Prefer the master Brand row id from `publicBrands` when loaded so filtering matches the DB;
 * fall back to public slug strings (same env defaults as `/property/by-brand` on home).
 */
/**
 * Numeric Brand.id for client-side listing filter (empty if unknown).
 */
export function resolveBrandIdForClientFilter(variant, publicBrands = []) {
  if (variant !== BRAND_VARIANT.STAY_JADE && variant !== BRAND_VARIANT.RED_OLIVE) {
    return '';
  }
  for (const b of publicBrands || []) {
    if (classifyBrandRow(b) !== variant) continue;
    const id = b?.id;
    if (id != null && /^\d+$/.test(String(id).trim())) {
      return String(id).trim();
    }
  }
  return '';
}

/**
 * Slug sent to GET /property/consumer `brand` when the catalog row has no numeric id yet.
 */
export function resolveConsumerBrandSlugParam(variant) {
  if (variant === BRAND_VARIANT.STAY_JADE) return PUBLIC_BRAND_STAYJADE;
  if (variant === BRAND_VARIANT.RED_OLIVE) return PUBLIC_BRAND_RED_OLIVE;
  return null;
}

export function resolveConsumerBrandQueryParam(variant, publicBrands = []) {
  if (variant !== BRAND_VARIANT.STAY_JADE && variant !== BRAND_VARIANT.RED_OLIVE) {
    return null;
  }
  const numericId = resolveBrandIdForClientFilter(variant, publicBrands);
  if (numericId) return numericId;

  for (const b of publicBrands || []) {
    if (classifyBrandRow(b) === variant) {
      const slug = String(b?.slug || '').trim();
      if (slug) return slug;
    }
  }
  return resolveConsumerBrandSlugParam(variant);
}

export function parsePropertyBrandTokens(raw) {
  if (raw == null || raw === '') return { ids: [], values: [] };

  let values = [];
  if (Array.isArray(raw)) {
    values = raw;
  } else {
    const stringValue = String(raw).trim();
    if (!stringValue) return { ids: [], values: [] };
    try {
      const parsed = JSON.parse(stringValue);
      values = Array.isArray(parsed) ? parsed : stringValue.split(',');
    } catch {
      values = stringValue.split(',');
    }
  }

  const ids = [];
  const tokenValues = [];

  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .forEach((value) => {
      if (value && typeof value === 'object') {
        const id = value.id ?? value.value;
        if (id != null && /^\d+$/.test(String(id).trim())) {
          ids.push(String(id).trim());
        }
        const label = value.slug ?? value.name;
        if (label) tokenValues.push(String(label).trim());
        return;
      }
      const token = String(value).replace(/^["']|["']$/g, '').trim();
      if (!token) return;
      if (/^\d+$/.test(token)) ids.push(token);
      else tokenValues.push(token);
    });

  return {
    ids: Array.from(new Set(ids)),
    values: Array.from(new Set(tokenValues.map((v) => v.toLowerCase()))),
  };
}

/**
 * Client-side brand match — only for unfiltered lists (e.g. mixing cached rows).
 * Listing pages pass `consumer?brand=` so the API is authoritative; do not re-filter those results.
 */
export function propertyMatchesBrandFilter(property, variant, publicBrands = []) {
  if (!variant || variant === BRAND_VARIANT.JADE_CAPS) return true;

  const { ids, values } = parsePropertyBrandTokens(property?.brands);
  const consumerBrand = resolveConsumerBrandQueryParam(variant, publicBrands);
  const catalogBrandId = resolveBrandIdForClientFilter(variant, publicBrands);

  const targetIds = new Set();
  if (consumerBrand && /^\d+$/.test(String(consumerBrand))) {
    targetIds.add(String(consumerBrand));
  }
  if (catalogBrandId) targetIds.add(catalogBrandId);

  if (targetIds.size > 0 && ids.some((id) => targetIds.has(id))) {
    return true;
  }

  const compactTargets = new Set();
  const slug = resolveConsumerBrandSlugParam(variant);
  if (slug) compactTargets.add(normalizeBrandQuery(slug));
  const row = resolvePublicBrandRowForVariant(variant, publicBrands);
  if (row?.slug) compactTargets.add(normalizeBrandQuery(row.slug));
  if (row?.name) compactTargets.add(normalizeBrandQuery(row.name));

  if (ids.length === 0 && values.length === 0) {
    return true;
  }

  return values.some((v) => compactTargets.has(normalizeBrandQuery(v)));
}

/** Master brand row from GET /brand/get (uses `data.rows`) — logos, slug, id. */
export function resolvePublicBrandRowForVariant(variant, publicBrands = []) {
  if (!variant || variant === BRAND_VARIANT.JADE_CAPS) return null;
  for (const b of publicBrands || []) {
    if (classifyBrandRow(b) === variant) return b;
  }
  return null;
}
